/**
 * Reads the reason out of a failed admin API call.
 *
 * The backend's GlobalExceptionFilter answers every error with a top-level
 * `message` (a string, or a list for some hand-thrown errors) and, for a
 * rejected body (422), a flat `validationErrors` map of field -> message.
 * Pages used to throw that away and show a generic toast, which hid why a
 * save failed (e.g. "minBuildVersion must be a whole build number").
 */

interface ApiErrorBody {
  message?: unknown;
  validationErrors?: unknown;
}

function responseBody(error: unknown): ApiErrorBody | undefined {
  if (typeof error !== 'object' || error === null || !('response' in error)) {
    return undefined;
  }
  const { response } = error as { response?: unknown };
  if (typeof response !== 'object' || response === null || !('data' in response)) {
    return undefined;
  }
  const { data } = response as { data?: unknown };
  return typeof data === 'object' && data !== null ? (data as ApiErrorBody) : undefined;
}

/** The `validationErrors` map of a 422, or `{}` when there is none. */
export function apiFieldErrors(error: unknown): Record<string, string> {
  const map = responseBody(error)?.validationErrors;
  if (typeof map !== 'object' || map === null || Array.isArray(map)) {
    return {};
  }
  const out: Record<string, string> = {};
  for (const [field, value] of Object.entries(map)) {
    if (typeof value === 'string' && value) {
      out[field] = value;
    }
  }
  return out;
}

/** The server's reason for a failed call, or `fallback` when it sent none. */
export function apiErrorMessage(error: unknown, fallback: string): string {
  const message = responseBody(error)?.message;
  if (typeof message === 'string' && message.trim()) {
    return message;
  }
  if (Array.isArray(message)) {
    const parts = message.filter((m): m is string => typeof m === 'string' && m.trim() !== '');
    if (parts.length) {
      return parts.join('; ');
    }
  }
  const fieldMessages = Object.values(apiFieldErrors(error));
  if (fieldMessages.length) {
    return fieldMessages.join('; ');
  }
  return fallback;
}

/** The HTTP status of a failed admin API call, or undefined without a response. */
export function apiErrorStatus(error: unknown): number | undefined {
  if (typeof error !== 'object' || error === null || !('response' in error)) {
    return undefined;
  }
  const { response } = error as { response?: { status?: unknown } };
  return typeof response?.status === 'number' ? response.status : undefined;
}

/**
 * Message for a report card that failed to load: the server's own reason when
 * it refused the request with a 400 (e.g. "The window is 120 days; at most 92
 * are allowed"), else `fallback`.
 */
export function reportLoadErrorMessage(error: unknown, fallback: string): string {
  return apiErrorStatus(error) === 400 ? apiErrorMessage(error, fallback) : fallback;
}
